import {createStore, StoreApi, useStore, UseBoundStore} from 'zustand';
import {BootstrapData} from '@ui/bootstrap-data/bootstrap-data';

export interface BootstrapDataState {
  data: BootstrapData;
  setData: (data: BootstrapData | string) => void;
  mergeData: (data: Partial<BootstrapData>) => void;
}

// Not: normalde `create()` kullanılırdı; ancak SSR (Konu 1) için `createStore` +
// `useStore` ile açık kurup `api` referansını elde ediyoruz (aşağıdaki
// getServerState yaması `api` üzerinde OLMALI — `create`'in döndürdüğü bound
// hook'a yazmak işe yaramaz, çünkü useStore içeride `api`yi okur). Bunun dışında
// davranış `create` ile birebir aynıdır.
const bootstrapApi: StoreApi<BootstrapDataState> = createStore<BootstrapDataState>()(
  set => ({
    // set bootstrap data that was provided with initial request from backend
    data:
      typeof window !== 'undefined' && window.bootstrapData
        ? decodeBootstrapData(window.bootstrapData)
        : null!,
    setData: data => {
      const decodedData =
        typeof data === 'string' ? decodeBootstrapData(data) : data;
      set({data: decodedData});
    },
    mergeData: (partial: Partial<BootstrapData>) => {
      set(state => ({data: {...state.data, ...partial}}));
    },
  }),
);

// SSR (Konu 1): React 18 renderToPipeableStream, zustand useStore'un SUNUCU
// snapshot'ı için `api.getServerState || api.getInitialState` kullanır
// (zustand ≥4.5). getInitialState store yaratılış anındaki durumu döndürür →
// Node'da (window yok) bu `data: null`'dır; yani SSR sırasında setBootstrapData
// ile yazılan istek-başı bootstrap verisi GÖRÜNMEZ (ThemeProvider/useAuth/
// useSettings null okur → SSR crash). getServerState'i getState'e bağlayarak
// sunucu render'ı CANLI (set edilmiş) durumu okur. YALNIZ sunucuda uygulanır →
// tarayıcı davranışı birebir korunur (client hydration zaten
// window.bootstrapData'dan dolu initial state okur).
if (typeof window === 'undefined') {
  (
    bootstrapApi as unknown as {getServerState?: () => BootstrapDataState}
  ).getServerState = bootstrapApi.getState;
}

export const useBootstrapDataStore = ((
  selector?: (state: BootstrapDataState) => unknown,
  equalityFn?: (a: unknown, b: unknown) => boolean,
) => useStore(bootstrapApi, selector as never, equalityFn as never)) as UseBoundStore<
  StoreApi<BootstrapDataState>
>;
Object.assign(useBootstrapDataStore, bootstrapApi);

export const getBootstrapData = () => useBootstrapDataStore.getState().data;
export const setBootstrapData = useBootstrapDataStore.getState().setData;
export const mergeBootstrapData = useBootstrapDataStore.getState().mergeData;

export function decodeBootstrapData(
  data: string | BootstrapData,
): BootstrapData {
  return typeof data === 'string' ? JSON.parse(data) : data;
}
